import { useCallback, useEffect, useMemo } from 'react';
import { CURRENCY_USD } from 'constants/currency';
import { EMPTY_COACH_AVATAR_SRC } from 'constants/user';
import {
  CoachSports,
  GetCoachesBySportAndGeoQuery,
  SportsEnum,
  useGetCoachesByNameSearchLazyQuery,
  useGetCoachesBySportAndGeoLazyQuery,
  useGetTopRatedCoachesBySportLazyQuery,
} from 'types/generated/client';
import { RawCoach, calculateCoachScore, hasProfilePhoto } from 'utils/shared/coach/coachScore';
import { calculateHaversineDistance } from 'utils/shared/geo/calculateHaversineDistance';
import { milesToMeters } from 'utils/shared/geo/milesToMeters';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import { useGeoLocation } from 'hooks/useGeoLocation';
import { VENUE_DISTANCE_IMPERIAL_OPTIONS } from 'components/Explore/Lessons/utils';

interface UseNearSportProp {
  sports: SportsEnum[];
  /** When set (>= 2 chars), name-search across coaches overrides the nearby list. */
  searchTerm?: string;
  /** Nearby search radius; defaults to the marketplace's 50 mi. */
  radiusMiles?: number;
}

const TARGET_COACH_COUNT = 5;
const SEARCH_LIMIT = 20;
const MIN_SEARCH_LENGTH = 2;

const useNearSport = ({
  sports,
  searchTerm,
  radiusMiles = VENUE_DISTANCE_IMPERIAL_OPTIONS[2].id,
}: UseNearSportProp) => {
  const { position, centerLatitude, centerLongitude } = useGeoLocation();

  const trimmedSearch = (searchTerm ?? '').trim();
  const isSearching = trimmedSearch.length >= MIN_SEARCH_LENGTH;

  const [getCoachesBySportAndGeoLazyQuery, { data: nearbyCoachesData, loading: nearbyLoading }] =
    useGetCoachesBySportAndGeoLazyQuery();

  const [
    getTopRatedCoachesBySportLazyQuery,
    { data: topRatedCoachesData, loading: topRatedLoading },
  ] = useGetTopRatedCoachesBySportLazyQuery();

  const [searchCoachesByName, { data: searchCoachesData, loading: searchLoading }] =
    useGetCoachesByNameSearchLazyQuery();

  useEffect(() => {
    if (!isSearching) return;
    searchCoachesByName({
      fetchPolicy: 'network-only',
      variables: { sports, search: `%${trimmedSearch}%`, limit: SEARCH_LIMIT },
    });
  }, [isSearching, trimmedSearch, sports, searchCoachesByName]);

  const fetchNearbyCoaches = useCallback(
    ({
      longitude,
      latitude,
      distance,
    }: {
      longitude: number;
      latitude: number;
      distance: number;
    }) => {
      getCoachesBySportAndGeoLazyQuery({
        fetchPolicy: 'network-only',
        variables: {
          sports,
          distance,
          from: {
            type: 'Point',
            coordinates: [longitude, latitude],
          },
        },
      });
    },
    [getCoachesBySportAndGeoLazyQuery, sports],
  );

  const fetchTopRatedCoaches = useCallback(() => {
    getTopRatedCoachesBySportLazyQuery({
      fetchPolicy: 'network-only',
      variables: {
        sports,
        // Over-fetch so we have headroom after dedup with nearby coaches.
        limit: TARGET_COACH_COUNT * 2,
      },
    });
  }, [getTopRatedCoachesBySportLazyQuery, sports]);

  const processCoachData = useCallback(
    (coachData: GetCoachesBySportAndGeoQuery['userProfiles'], isNearby = true) => {
      return coachData.map((coach) => {
        const distance = isNearby
          ? calculateHaversineDistance({
              coord1: {
                latitude: centerLatitude || 0,
                longitude: centerLongitude || 0,
              },
              coord2: {
                latitude: coach.geometry.coordinates[1],
                longitude: coach.geometry.coordinates[0],
              },
              unit: 'miles',
            })
          : null;

        return {
          id: coach.id,
          username: coach.username as string,
          photoUrl: getProfileImageUrlOrPlaceholder({
            path: coach.profileImagePath || '',
            placeholder: EMPTY_COACH_AVATAR_SRC,
          }),
          certificationImages: [],
          reviews: {
            averageRating:
              (coach.coachReviewsAggregate?.aggregate?.avg?.rating as number | null) || 0,
            totalReviews: (coach.coachReviewsAggregate?.aggregate?.count as number | null) || 0,
          },
          defaultSport: coach.defaultSport || '',
          coachSports: coach.coachSports as CoachSports[],
          fullName: coach.fullName || '',
          price: coach.priceUnitAmountCoachDefault || 0,
          lessonGiven: coach.coachLessonsAggregate?.aggregate?.count || 0,
          distance,
          isProfessionalRacquetRegistryActive: coach.isProfessionalRacquetRegistryActive || false,
          currency: coach.stripeMerchantCurrency || coach.currencyCoachDefault || CURRENCY_USD,
          isLocalCoach: isNearby,
          originalData: coach,
        };
      });
    },
    [centerLatitude, centerLongitude],
  );

  const { decoratedCoaches, hasLocalCoaches } = useMemo(() => {
    // Search mode: name-matched coaches replace the nearby list entirely.
    if (isSearching) {
      const results = processCoachData(
        (searchCoachesData?.userProfiles ?? []) as GetCoachesBySportAndGeoQuery['userProfiles'],
        false,
      );
      return { decoratedCoaches: results.slice(0, SEARCH_LIMIT), hasLocalCoaches: false };
    }

    const nearbyCoaches = nearbyCoachesData?.userProfiles || [];
    const topRatedCoaches = (topRatedCoachesData?.userProfiles || []) as RawCoach[];

    const sortByScore = (coaches: RawCoach[]) =>
      coaches
        .filter(hasProfilePhoto)
        .slice()
        .sort((a, b) => calculateCoachScore(b) - calculateCoachScore(a));

    const processedNearby = processCoachData(sortByScore(nearbyCoaches), true);

    if (processedNearby.length >= TARGET_COACH_COUNT) {
      return {
        decoratedCoaches: processedNearby.slice(0, TARGET_COACH_COUNT),
        hasLocalCoaches: true,
      };
    }

    // Top up with top-rated coaches that aren't already in the nearby list.
    const nearbyIds = new Set(processedNearby.map((coach) => coach.id));
    const fillers = processCoachData(
      sortByScore(topRatedCoaches).filter((coach) => !nearbyIds.has(coach.id)),
      false,
    );

    return {
      decoratedCoaches: [...processedNearby, ...fillers].slice(0, TARGET_COACH_COUNT),
      hasLocalCoaches: processedNearby.length > 0,
    };
  }, [
    isSearching,
    searchCoachesData?.userProfiles,
    nearbyCoachesData?.userProfiles,
    topRatedCoachesData?.userProfiles,
    processCoachData,
  ]);

  useEffect(() => {
    if (position) {
      fetchNearbyCoaches({
        longitude: position.longitude,
        latitude: position.latitude,
        distance: milesToMeters(radiusMiles),
      });
    }
  }, [position, radiusMiles, fetchNearbyCoaches]);

  useEffect(() => {
    if (
      nearbyCoachesData &&
      (nearbyCoachesData.userProfiles || []).filter(hasProfilePhoto).length < TARGET_COACH_COUNT
    ) {
      fetchTopRatedCoaches();
    }
  }, [nearbyCoachesData, fetchTopRatedCoaches]);

  useEffect(() => {
    if (!position) {
      fetchTopRatedCoaches();
    }
  }, [position, fetchTopRatedCoaches]);

  return {
    decoratedCoaches,
    loading: isSearching ? searchLoading : nearbyLoading || topRatedLoading,
    hasLocalCoaches,
  };
};

export default useNearSport;
